<script setup lang="ts">
  import { ref, computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { DataTableColumnPicker, type ToggleableColumn } from "@/components/episto-ui/data-table";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { useHistoStore } from "@stores/histo";
  import { useHistoTable } from "@composables/useHistoTable";
  import { HistoDataTable } from "@/components/Admin/Histo/HistoDataTable";
  import { HistoDetailSheet } from "@/components/Admin/Histo/HistoDetailSheet";
  import HistoAiSearch from "@/components/Admin/Histo/HistoAiSearch.vue";
  import { useHistoFilters, TOGGLEABLE_COLUMNS, HIDDEN_BY_DEFAULT_COLUMNS } from "@/utils/composables/filters/useHistoFilters";
  import type { Histo, HistoTableRow } from "@/components/Admin/Histo/HistoDataTable/types";
  import type { VisibilityState } from "@tanstack/vue-table";

  const histoStore = useHistoStore();
  const { histos, pagination } = storeToRefs(histoStore);
  const { fetchHistos } = histoStore;
  const { transformHistosToTableRows } = useHistoTable();

  const {
    filterConfigs,
    filterValues,
    searchQuery,
    isLoading,
    aiTargetInput,
    aiTopicInput,
    aiTarget,
    aiTopic,
    isAiActive,
    aiError,
    applyAiSearch,
    cancelAiSearch,
    retryAiSearch,
    clearAiSearch,
    clearFilters,
    buildFiltersPayload,
  } = useHistoFilters();

  const isSheetOpen = ref(false);
  const selectedHisto = ref<Histo | null>(null);
  const columnVisibility = ref<VisibilityState>({
    similarity: false,
    cpm: false,
    ctr: false,
    cpc: false,
    startRate: false,
    cps: false,
  });

  const tableData = computed<HistoTableRow[]>(() => {
    return transformHistosToTableRows(histos.value as Histo[]);
  });

  watch(isAiActive, (active) => {
    columnVisibility.value = { ...columnVisibility.value, similarity: active };
  });

  const paginationInfo = computed(() => ({
    currentPage: pagination.value.currentPage,
    perPage: pagination.value.perPage,
    totalCount: pagination.value.totalCount,
    totalPages: pagination.value.totalPages,
  }));

  const pickerColumns: ToggleableColumn[] = TOGGLEABLE_COLUMNS.map(({ id, label }) => ({
    key: id,
    label,
    hiddenByDefault: HIDDEN_BY_DEFAULT_COLUMNS.includes(id),
  }));

  async function handlePageChange(page: number) {
    const filters = buildFiltersPayload();
    await fetchHistos({
      filters,
      query: searchQuery.value,
      target: aiTarget.value || null,
      topic: aiTopic.value || null,
      page,
    });
  }

  function openDetailSheet(row: HistoTableRow) {
    selectedHisto.value = row.originalHisto;
    isSheetOpen.value = true;
  }
</script>

<template>
  <IndexPageLayout
    title="Histo"
    padding="none">
    <template #banner>
      <HistoAiSearch
        v-model:target="aiTargetInput"
        v-model:topic="aiTopicInput"
        :loading="isLoading"
        :active="isAiActive"
        :error="aiError"
        :result-count="paginationInfo.totalCount"
        :applied-target="aiTarget"
        :applied-topic="aiTopic"
        @search="applyAiSearch"
        @cancel="cancelAiSearch"
        @retry="retryAiSearch"
        @clear="clearAiSearch" />
    </template>

    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Filter by keyword">
        <template #filters>
          <FilterDropdown
            v-model="filterValues"
            :filters="filterConfigs"
            @clear="clearFilters" />
          <DataTableColumnPicker
            v-model="columnVisibility"
            :columns="pickerColumns" />
        </template>
      </IndexPageFilters>
    </template>

    <div class="flex-1 overflow-hidden">
      <HistoDataTable
        :data="tableData"
        :loading="isLoading"
        :column-visibility="columnVisibility"
        :pagination="paginationInfo"
        @row-click="openDetailSheet"
        @page-change="handlePageChange" />
    </div>
    <HistoDetailSheet
      v-model:open="isSheetOpen"
      :histo="selectedHisto" />
  </IndexPageLayout>
</template>
